import React, { useState } from 'react';
import { Chapter, Level, UserProgress } from '../types';
import { getDifficultyLabel, getDifficultyColor } from '../utils';

interface ChallengePageProps {
  levels: Level[];
  chapters: Chapter[];
  progress: UserProgress | null;
  onSelectLevel: (level: Level) => void;
}

const ChallengePage: React.FC<ChallengePageProps> = ({
  levels,
  chapters,
  progress,
  onSelectLevel
}) => {
  const [selectedChapter, setSelectedChapter] = useState<number>(1);

  const filteredLevels = levels.filter(l => l.chapter === selectedChapter);
  const currentChapter = chapters.find(c => c.id === selectedChapter);

  const chapterProgress = {
    completed: filteredLevels.filter(l => 
      progress?.completedLevels.includes(l.id)
    ).length,
    total: filteredLevels.length
  };

  return (
    <div className="fade-in">
      <div className="header">
        <h1>🎯 闯关挑战</h1>
        <div className="subtitle">选择关卡开始挑战</div>
      </div>

      <div className="main-content">
        {/* 章节切换 */}
        <div className="tabs">
          {chapters.map(chapter => (
            <button
              key={chapter.id}
              className={`tab-item ${selectedChapter === chapter.id ? 'active' : ''}`}
              onClick={() => setSelectedChapter(chapter.id)}
            >
              第{chapter.id}章
            </button>
          ))}
        </div>

        {/* 章节信息 */}
        <div className="chapter-card">
          <div style={{ marginBottom: '16px' }}>
            <div style={{ 
              fontSize: '20px', 
              fontWeight: '600', 
              color: '#1f2937',
              marginBottom: '8px'
            }}>
              {currentChapter?.title}
            </div>
            <div style={{ 
              fontSize: '14px', 
              color: '#6b7280',
              marginBottom: '16px'
            }}>
              {currentChapter?.subtitle}
            </div>
            
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '12px',
              background: '#f3f4f6',
              borderRadius: '8px'
            }}>
              <div>
                <div style={{ fontSize: '24px', fontWeight: '700', color: '#3b82f6' }}>
                  {chapterProgress.completed}/{chapterProgress.total}
                </div>
                <div style={{ fontSize: '12px', color: '#6b7280' }}>已完成关卡</div>
              </div>
              <div>
                <div style={{ fontSize: '24px', fontWeight: '700', color: '#8b5cf6' }}>
                  {currentChapter?.totalQuestions}
                </div>
                <div style={{ fontSize: '12px', color: '#6b7280' }}>总题数</div>
              </div>
            </div>
          </div>
        </div>

        {/* 关卡列表 */}
        {filteredLevels.map(level => {
          const difficultyColor = getDifficultyColor(level.difficulty);
          const difficultyLabel = getDifficultyLabel(level.difficulty);
          
          return (
            <div 
              key={level.id}
              className="level-item"
              onClick={() => onSelectLevel(level)}
              style={{
                background: level.completed ? '#d1fae5' : '#ffffff',
                border: level.completed ? '2px solid #10b981' : '2px solid transparent',
                cursor: 'pointer'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={{
                  width: '48px',
                  height: '48px',
                  borderRadius: '12px',
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  fontSize: '20px',
                  fontWeight: '700'
                }}>
                  {level.id}
                </div>
                
                <div className="level-info">
                  <div className="level-name">{level.title}</div>
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '8px',
                    marginTop: '4px'
                  }}>
                    <span style={{
                      padding: '2px 8px',
                      background: difficultyColor,
                      color: 'white',
                      borderRadius: '4px',
                      fontSize: '11px',
                      fontWeight: '500'
                    }}>
                      {difficultyLabel}
                    </span>
                    <span style={{ fontSize: '12px', color: '#6b7280' }}>
                      {level.questions.length}题
                    </span>
                  </div>
                  <div className="level-progress" style={{ marginTop: '4px' }}>
                    最低通过分数：{level.requiredScore}
                  </div>
                </div>
              </div>

              <div className="level-status">
                <div className="stars">
                  {level.stars === 0 ? '☆' : '⭐'.repeat(level.stars)}
                  {level.stars < 3 ? '☆'.repeat(3 - level.stars) : ''}
                </div>
                {level.completed && (
                  <div style={{ fontSize: '20px' }}>✅</div>
                )}
              </div>
            </div>
          );
        })}

        {/* 闯关规则 */}
        <div className="chapter-card" style={{ marginTop: '20px' }}>
          <div className="chapter-title" style={{ marginBottom: '12px' }}>
            📜 闯关规则
          </div>
          <div style={{ fontSize: '14px', lineHeight: '1.8', color: '#6b7280' }}>
            <div style={{ marginBottom: '8px' }}>
              <strong>• 答题时间：</strong>每题90秒
            </div>
            <div style={{ marginBottom: '8px' }}>
              <strong>• 通过条件：</strong>正确率≥75%
            </div>
            <div style={{ marginBottom: '8px' }}>
              <strong>• 星级评定：</strong>
              <br />
              ⭐⭐⭐ 正确率≥90%
              <br />
              ⭐⭐ 正确率≥75%
              <br />
              ⭐ 正确率≥60%
            </div>
            <div>
              <strong>• 自由挑战：</strong>所有关卡已解锁，可自由选择
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChallengePage;
